import React from 'react';

import {Item, VehicleCover, VehicleTrait, VehicleTraitType} from '../../generated/types';
import {MonsterTrait} from '../../pages/monsters';
import {MonsterBlockTable} from '../monster-block-table';
import {getPounds, toMonsterImmune} from '../render-helpers';
import {humanNumber, wordByCount} from '../utils';
import {MarkdownInline} from '../utils/md';

import markdownCss from '../utils/md/markdown.module.css';
import css from '../monster-view/monster-view.module.css';

type Props = {
	item: Pick<
		Item,
		| 'vehAbilities'
		| 'vehicleTraits'
		| 'vehAc'
		| 'vehAcNote'
		| 'vehHp'
		| 'vehDmgThresh'
		| 'vehMishapThresh'
		| 'speed'
		| 'capCreature'
		| 'capCargoLb'
		| 'immune'
		| 'conditionImmune'
	>;
};

const CoverNames: Record<VehicleCover, string> = {
	[VehicleCover.Half]: 'укрытие наполовину',
	[VehicleCover.ThreeQuarters]: 'укрытие на три четверти',
	[VehicleCover.Total]: 'полное укрытие',
};

const TraitGroups: Array<[VehicleTraitType, string | null]> = [
	[VehicleTraitType.Feature, null],
	[VehicleTraitType.Station, 'Действия боевых постов'],
	[VehicleTraitType.Reaction, 'Ответные действия'],
];

function toStationTitle(trait: VehicleTrait) {
	const terms = [];
	if (trait.crew)
		terms.push(
			`требуется ${wordByCount(trait.crew, ['член', 'члена', 'членов'], true)} экипажа` +
				(trait.count && trait.count > 1 ? ' для каждого' : ''),
		);
	if (trait.cover) terms.push(CoverNames[trait.cover]);

	return terms.length ? `${trait.title} (${terms.join(', ')})` : trait.title;
}

function toHp(item: Props['item']) {
	const limits = [];
	if (item.vehDmgThresh) limits.push(`предел прочности ${item.vehDmgThresh}`);
	if (item.vehMishapThresh) limits.push(`предел сбоя ${item.vehMishapThresh}`);

	return limits.length ? `${item.vehHp} (${limits.join(', ')})` : item.vehHp;
}

export const VehicleStatblock: React.FC<Props> = ({item}) => {
	const toImmuneItems = (types?: string[]) => types?.map((type) => ({type}));

	return (
		<div className={css.wrapper}>
			<div className={markdownCss.margin}>
				<div className={markdownCss.balanced}>
					<div className={markdownCss.indent}>
						{item.capCreature && (
							<p>
								<b>Существа</b>{' '}
								{wordByCount(item.capCreature, ['существо', 'существа', 'существ'], true)} среднего
								размера
							</p>
						)}
						{item.capCargoLb && (
							<p>
								<b>Груз</b> {getPounds(item.capCargoLb)}
							</p>
						)}
						<p>
							<b>КБ</b> {item.vehAc}
							{item.vehAcNote && ` (${item.vehAcNote})`}
						</p>
						<p>
							<b>ПЗ</b> {toHp(item)}
						</p>
						{item.speed && (
							<p>
								<b>Скорость</b> {humanNumber(item.speed)}{' '}
								{wordByCount(item.speed, ['фут', 'фута', 'футов'])}
							</p>
						)}
					</div>
				</div>
			</div>

			{item.vehAbilities && <MonsterBlockTable params={item.vehAbilities} />}

			{(item.immune || item.conditionImmune) && (
				<div className={markdownCss.margin}>
					<div className={markdownCss.indent}>
						<p>
							<b>Невосприимчивости</b>{' '}
							<MarkdownInline
								text={toMonsterImmune(toImmuneItems(item.immune), toImmuneItems(item.conditionImmune))}
							/>
						</p>
					</div>
				</div>
			)}

			{TraitGroups.map(([type, heading]) => {
				const traits = item.vehicleTraits?.filter((trait) => trait.type === type);
				if (!traits?.length) return null;

				return (
					<React.Fragment key={type}>
						{heading && <h2>{heading}</h2>}
						{traits.map((trait) => (
							<MonsterTrait
								key={trait.title}
								title={type === VehicleTraitType.Station ? toStationTitle(trait) : trait.title}
								description={trait.description}
							/>
						))}
					</React.Fragment>
				);
			})}
		</div>
	);
};
